/**
 * The height beacon for an embedded form (U6).
 *
 * A form framed on somebody else's page cannot size its own iframe; the
 * page's copy of embed.js can, if the form tells it how tall it is. So a
 * framed form posts { type: 'form-magic:height', height } to its parent
 * whenever its document changes size. Never when it is the top window: there
 * is nobody to tell, and posting to oneself is noise.
 *
 * The target origin is '*' on purpose — the only thing in the message is a
 * number, and the embedding page's origin is not something the form knows.
 */

export const HEIGHT_MESSAGE = 'form-magic:height';

export interface HeightMessage { type: typeof HEIGHT_MESSAGE; height: number }

/**
 * The content's height. With a selector, the bottom edge of that element —
 * the public page's `.of-page` — because the document itself is padded out
 * to the viewport (`min-height: 100vh`) and would only ever report the
 * frame's own height back, so a frame could grow but never shrink. Without
 * one, the tallest of the measures browsers disagree on.
 */
export function measureHeight(doc: Document, selector?: string, win: Window = window): number {
  const el = selector ? doc.querySelector(selector) : null;
  if (el) {
    const rect = el.getBoundingClientRect();
    return Math.ceil(rect.bottom + (win.scrollY || 0));
  }
  const root = doc.documentElement;
  const body = doc.body;
  return Math.ceil(Math.max(
    root?.scrollHeight || 0, root?.offsetHeight || 0,
    body?.scrollHeight || 0, body?.offsetHeight || 0,
  ));
}

/** Is this document showing inside another page's frame? */
export function isFramed(win: Window = window): boolean {
  try { return win.parent !== win; } catch { return true; }
}

export interface BeaconOptions {
  /** the element whose bottom edge is the height — see measureHeight */
  selector?: string;
  /** how often to re-measure regardless of observers (ms); 0 disables */
  pollMs?: number;
}

/**
 * Start posting the height to the parent; returns the stop function. A no-op
 * (and an immediate no-op stop) when the window is not framed.
 *
 * A ResizeObserver watches the document and the measured element; a slow
 * poll backs it up, because the observed element is re-rendered by the page
 * (the loading, gate, form and closed branches are different elements) and
 * a shrink below the viewport moves nothing the observer is attached to.
 */
export function startHeightBeacon(win: Window = window, doc: Document = document, opts: BeaconOptions = {}): () => void {
  if (!isFramed(win)) return () => undefined;
  const { selector, pollMs = 1000 } = opts;
  let last = -1;
  const post = () => {
    const height = measureHeight(doc, selector, win);
    if (height === last) return;
    last = height;
    try {
      const msg: HeightMessage = { type: HEIGHT_MESSAGE, height };
      win.parent.postMessage(msg, '*');
    } catch { /* a parent that refuses messages is not the respondent's problem */ }
  };
  const Observer = (win as unknown as { ResizeObserver?: typeof ResizeObserver }).ResizeObserver;
  let ro: ResizeObserver | null = null;
  let watched: Element | null = null;
  const watchTarget = () => {
    if (!ro || !selector) return;
    const el = doc.querySelector(selector);
    if (el && el !== watched) { ro.observe(el); watched = el; }
  };
  if (typeof Observer === 'function') {
    ro = new Observer(() => post());
    if (doc.documentElement) ro.observe(doc.documentElement);
    if (doc.body) ro.observe(doc.body);
  }
  const tick = () => { watchTarget(); post(); };
  tick();
  const timer = pollMs > 0 || !ro ? win.setInterval(tick, pollMs > 0 ? pollMs : 500) : null;
  win.addEventListener('load', tick);
  return () => {
    ro?.disconnect();
    if (timer !== null) win.clearInterval(timer);
    win.removeEventListener('load', tick);
  };
}
